import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgModule,
  NgZone,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { ForModule } from '@rx-angular/template';
import { filter, map, Observable, take, tap } from 'rxjs';
import { MainMenuTreeItem } from '@nx-icilalune/data/icilalune/types';
import { TreeItem } from '@nx-icilalune/drupal-types';
import { ComponentStore } from '@ngrx/component-store';
import { LetModule } from '@ngrx/component';

export interface TabsMenuItem {
  id: string;
  label: string;
}

interface TabsMenuStore {
  tabs?: TreeItem<MainMenuTreeItem>[] | undefined;
  selected?: TreeItem<MainMenuTreeItem> | undefined;
  selectedIndex?: number | undefined;
}

@Component({
  selector: 'i2l-ui-tabs-menu',
  template: `
    <ng-container *ngrxLet="vm$; let vm">
      <ng-container *ngIf="vm.tabs as tabs">
        <ul>
          <li *rxFor="let tab of tabs; let index = index">
            <button
              #tabButton
              [class.active]="vm.selected?.ref?.id === tab.ref.id"
              (click)="setSelectedItem(tab, index)"
            >
              <span [innerHTML]="tab.title"></span>
            </button>
          </li>
        </ul>
      </ng-container>
    </ng-container>
    <div class="hilite" #hilite></div>
  `,
  styleUrls: ['./tabs-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabsMenuComponent
  extends ComponentStore<TabsMenuStore>
  implements AfterViewInit
{
  readonly selected$ = this.select((s) => s.selected);
  readonly selectedIndex$ = this.select((s) => s.selectedIndex);
  readonly vm$ = this.select((s) => s);

  @ViewChild('hilite')
  hilite!: ElementRef<HTMLDivElement>;

  @Output()
  tabChanged = this.select((s) => s.selected);

  #hiliteInitialized = false;

  checkSelected = this.effect(
    (selected$: Observable<TreeItem<MainMenuTreeItem> | undefined>) => {
      return selected$.pipe(
        map((selected) => {
          const tabs = this.get((s) => s.tabs);
          if (selected && tabs) {
            const index = tabs.findIndex(
              (tab) => tab.ref.id === selected.ref.id
            );
            if (this.hilite && this.tabButtons.length > index) {
              const targetElement: HTMLButtonElement | undefined =
                this.tabButtons.get(index)?.nativeElement;
              /* this.#animateHilite(
               targetElement,
               this.#hiliteInitialized ? 0.4 : 0
             );*/
              if (this.#hiliteInitialized) {
                this.hilite.nativeElement.style.setProperty(
                  '--lune-hilite-transition-duration',
                  '0.4s'
                );
              }
              if (targetElement) {
                this.hilite.nativeElement.style.setProperty(
                  '--lune-pos-tab-hilite-top',
                  `${targetElement.offsetHeight}px`
                );
                this.hilite.nativeElement.style.setProperty(
                  '--lune-pos-tab-hilite-left',
                  `${targetElement.offsetLeft}px`
                );
                this.hilite.nativeElement.style.setProperty(
                  '--lune-size-tab-hilite-width',
                  `${targetElement.offsetWidth}px`
                );
              }
              this.#hiliteInitialized = true;
            }
          }
        })
      );
    }
  );

  tabButtonsReady = this.effect((tabButtons$: Observable<QueryList<any>>) => {
    return tabButtons$.pipe(
      filter((changes) => {
        const tabs = this.get((s) => s.tabs);
        return tabs?.length === changes.length;
      }),
      take(1),
      tap(() => {
        this.checkSelected(this.selected$);
      })
    );
  });

  constructor(private ngZone: NgZone) {
    super({});

    /*this.hold(
      combineLatest([
        this.select(selectSlice(['selected', 'tabs'])),
        this.tabButtons$,
      ]),
      ([{ selected, tabs }, tabButtons]) => {
        if (selected && tabs) {
          const index = tabs.findIndex((tab) => tab.ref.id === selected.ref.id);
          if (this.hilite && tabButtons.length > index) {
            const targetElement: HTMLButtonElement | undefined =
              tabButtons.get(index)?.nativeElement;
            this.#animateHilite(
              targetElement,
              this.#hiliteInitialized ? 0.4 : 0
            );
            this.#hiliteInitialized = true;
          }
        }
      }
    );*/
  }

  @ViewChildren('tabButton') tabButtons!: QueryList<
    ElementRef<HTMLButtonElement>
  >;

  @Input() set tabs(tabs: TreeItem<MainMenuTreeItem>[] | undefined) {
    this.patchState({
      tabs,
      selected: tabs ? tabs[0] : undefined,
      selectedIndex: 0,
    });
  }

  setSelectedItem(tab: TreeItem<MainMenuTreeItem>, index: number) {
    this.patchState({ selected: tab, selectedIndex: index });
  }

  ngAfterViewInit(): void {
    this.tabButtonsReady(this.tabButtons.changes);
  }
}

@NgModule({
  imports: [CommonModule, TranslocoModule, ForModule, LetModule],
  declarations: [TabsMenuComponent],
  exports: [TabsMenuComponent],
})
export class TabsMenuComponentModule {}
